import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Select,
  InputNumber,
  message,
  Divider,
  Row,
  Col,
  Space,
  Upload,
  Avatar,
  Spin,
  Typography
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  UploadOutlined,
  UserOutlined,
  LockOutlined,
  SettingOutlined,
  BellOutlined,
  SecurityScanOutlined,
  MailOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const adminApi = axios.create({
  baseURL: '/api',
});
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const { Option } = Select;
const { TextArea } = Input;

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [otpSettings, setOtpSettings] = useState({});
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSaving, setOtpSaving] = useState('');

  useEffect(() => {
    fetchOtpSettings();
  }, []);

  const fetchOtpSettings = async () => {
    setOtpLoading(true);
    try {
      const res = await adminApi.get('/config/otp-settings');
      const map = {};
      (res.data.settings || []).forEach(s => { map[s.setting_key] = s.setting_value; });
      setOtpSettings(map);
    } catch (err) {
      console.error('Failed to fetch OTP settings:', err);
    } finally {
      setOtpLoading(false);
    }
  };

  const updateOtpSetting = async (key, value) => {
    setOtpSaving(key);
    try {
      await adminApi.put(`/config/otp-settings/${key}`, { value: String(value) });
      setOtpSettings(prev => ({ ...prev, [key]: String(value) }));
      message.success('Pengaturan OTP diperbarui');
    } catch (err) {
      message.error('Gagal memperbarui pengaturan OTP');
    } finally {
      setOtpSaving('');
    }
  };

  const handleGeneralSubmit = async (values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Pengaturan umum berhasil diperbarui');
    } catch (error) {
      message.error('Gagal memperbarui pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Password baru tidak cocok');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await adminApi.post('/auth/change-password', {
        current_password: values.currentPassword,
        new_password: values.newPassword,
        confirm_password: values.confirmPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Password berhasil diperbarui');
      passwordForm.resetFields();
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Gagal memperbarui password';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSubmit = async (values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Pengaturan notifikasi berhasil diperbarui');
    } catch (error) {
      message.error('Gagal memperbarui pengaturan notifikasi');
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySubmit = async (values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Pengaturan keamanan berhasil diperbarui');
    } catch (error) {
      message.error('Gagal memperbarui pengaturan keamanan');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      key: 'general',
      icon: <SettingOutlined />,
      label: 'Pengaturan Umum',
    },
    {
      key: 'security',
      icon: <LockOutlined />,
      label: 'Keamanan',
    },
    // {
    //   key: 'notifications',
    //   icon: <BellOutlined />,
    //   label: 'Notifikasi',
    // },
    {
      key: 'system',
      icon: <SecurityScanOutlined />,
      label: 'Sistem',
    },
    {
      key: 'otp',
      icon: <MailOutlined />,
      label: 'OTP',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Pengaturan Sistem</h1>
        <p style={{ color: '#666' }}>Kelola konfigurasi sistem Arendo</p>
      </div>

      <Row gutter={[24, 24]}>
        {/* Sidebar Menu */}
        <Col xs={24} md={6}>
          <Card title="Menu Pengaturan" size="small">
            <div className="settings-menu">
              {menuItems.map(item => (
                <div
                  key={item.key}
                  className={`menu-item ${activeTab === item.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(item.key)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: activeTab === item.key ? '#1890ff' : 'transparent',
                    color: activeTab === item.key ? '#fff' : '#666'
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Content Area */}
        <Col xs={24} md={18}>
          {activeTab === 'general' && (
            <Card title="Pengaturan Umum">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleGeneralSubmit}
                initialValues={{
                  siteName: 'Arendo Dashboard',
                  siteDescription: 'Dashboard untuk manajemen Arendo',
                  adminEmail: 'admin@paymentgateway.com',
                  timezone: 'Asia/Jakarta',
                  currency: 'IDR',
                  language: 'id-ID',
                  maintenanceMode: false,
                  debugMode: false
                }}
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Nama Situs"
                      name="siteName"
                      rules={[{ required: true, message: 'Nama situs wajib diisi' }]}
                    >
                      <Input placeholder="Masukkan nama situs" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Email Admin"
                      name="adminEmail"
                      rules={[
                        { required: true, message: 'Email admin wajib diisi' },
                        { type: 'email', message: 'Format email tidak valid' }
                      ]}
                    >
                      <Input placeholder="admin@example.com" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Deskripsi Situs"
                  name="siteDescription"
                >
                  <TextArea rows={3} placeholder="Masukkan deskripsi situs" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Zona Waktu"
                      name="timezone"
                    >
                      <Select placeholder="Pilih zona waktu">
                        <Option value="Asia/Jakarta">WIB (GMT+7)</Option>
                        <Option value="Asia/Makassar">WITA (GMT+8)</Option>
                        <Option value="Asia/Jayapura">WIT (GMT+9)</Option>
                        <Option value="UTC">UTC (GMT+0)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Mata Uang"
                      name="currency"
                    >
                      <Select placeholder="Pilih mata uang">
                        <Option value="IDR">Rupiah (IDR)</Option>
                        <Option value="USD">US Dollar (USD)</Option>
                        <Option value="EUR">Euro (EUR)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Bahasa"
                      name="language"
                    >
                      <Select placeholder="Pilih bahasa">
                        <Option value="id-ID">Bahasa Indonesia</Option>
                        <Option value="en-US">English</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Mode Maintenance"
                      name="maintenanceMode"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Mode Debug"
                      name="debugMode"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button icon={<ReloadOutlined />}>Reset</Button>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                      Simpan Pengaturan
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          )}

          {activeTab === 'security' && (
            <>
              <Card title="Ubah Password" style={{ marginBottom: '24px' }}>
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordSubmit}
                >
                  <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Password Lama"
                        name="currentPassword"
                        rules={[{ required: true, message: 'Password lama wajib diisi' }]}
                      >
                        <Input.Password placeholder="Masukkan password lama" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Password Baru"
                        name="newPassword"
                        rules={[
                          { required: true, message: 'Password baru wajib diisi' },
                          { min: 8, message: 'Password minimal 8 karakter' }
                        ]}
                      >
                        <Input.Password placeholder="Masukkan password baru" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Konfirmasi Password Baru"
                        name="confirmPassword"
                        rules={[{ required: true, message: 'Konfirmasi password wajib diisi' }]}
                      >
                        <Input.Password placeholder="Masukkan kembali password baru" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                      Ubah Password
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              <Card title="Pengaturan Keamanan">
                <Form
                  layout="vertical"
                  onFinish={handleSecuritySubmit}
                  initialValues={{
                    sessionTimeout: 30,
                    maxLoginAttempts: 5,
                    lockoutDuration: 15,
                    twoFactorAuth: false,
                    ipWhitelist: '',
                    allowedIps: '192.168.1.1, 10.0.0.1'
                  }}
                >
                  <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Timeout Session (menit)"
                        name="sessionTimeout"
                      >
                        <InputNumber min={5} max={480} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Maksimal Percobaan Login"
                        name="maxLoginAttempts"
                      >
                        <InputNumber min={3} max={10} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Durasi Lockout (menit)"
                        name="lockoutDuration"
                      >
                        <InputNumber min={5} max={60} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Two-Factor Authentication"
                    name="twoFactorAuth"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                      Simpan Pengaturan Keamanan
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </>
          )}

          {activeTab === 'notifications' && (
            <Card title="Pengaturan Notifikasi">
              <Form
                layout="vertical"
                onFinish={handleNotificationSubmit}
                initialValues={{
                  emailNotifications: true,
                  smsNotifications: false,
                  pushNotifications: true,
                  transactionSuccess: true,
                  transactionFailed: true,
                  newRegistration: true,
                  systemAlerts: true,
                  maintenanceAlerts: true
                }}
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Notifikasi Email"
                      name="emailNotifications"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Notifikasi SMS"
                      name="smsNotifications"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Notifikasi Push"
                      name="pushNotifications"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <h4>Jenis Notifikasi</h4>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Transaksi Berhasil"
                      name="transactionSuccess"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Transaksi Gagal"
                      name="transactionFailed"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Registrasi Baru"
                      name="newRegistration"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Alert Sistem"
                      name="systemAlerts"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Alert Maintenance"
                      name="maintenanceAlerts"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    Simpan Pengaturan Notifikasi
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {activeTab === 'system' && (
            <Card title="Pengaturan Sistem">
              <Form
                layout="vertical"
                onFinish={handleSecuritySubmit}
                initialValues={{
                  maxFileSize: 10,
                  allowedFileTypes: 'jpg,jpeg,png,pdf,doc,docx',
                  databaseBackup: 'daily',
                  logRetention: 30,
                  apiRateLimit: 100,
                  cacheTimeout: 3600
                }}
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Maksimal Ukuran File (MB)"
                      name="maxFileSize"
                    >
                      <InputNumber min={1} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Backup Database"
                      name="databaseBackup"
                    >
                      <Select placeholder="Pilih frekuensi backup">
                        <Option value="daily">Harian</Option>
                        <Option value="weekly">Mingguan</Option>
                        <Option value="monthly">Bulanan</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Retensi Log (hari)"
                      name="logRetention"
                    >
                      <InputNumber min={7} max={365} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="API Rate Limit (req/menit)"
                      name="apiRateLimit"
                    >
                      <InputNumber min={10} max={1000} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Cache Timeout (detik)"
                      name="cacheTimeout"
                    >
                      <InputNumber min={60} max={86400} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Tipe File yang Diizinkan"
                  name="allowedFileTypes"
                >
                  <Input placeholder="jpg,jpeg,png,pdf,doc,docx" />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button>Test Koneksi Database</Button>
                    <Button>Clear Cache</Button>
                    <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                      Simpan Pengaturan Sistem
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          )}

          {activeTab === 'otp' && (
            <Card title="Pengaturan OTP" bordered={false}>
              <Spin spinning={otpLoading}>
                <Row gutter={[0, 24]}>
                  <Col span={24}>
                    <Card type="inner" title="Login Merchant">
                      <Row align="middle" justify="space-between">
                        <Col>
                          <div><strong>OTP saat Login</strong></div>
                          <Text type="secondary">Wajibkan kode OTP via email setiap merchant login</Text>
                        </Col>
                        <Col>
                          <Switch
                            checked={otpSettings['otp_login_enabled'] === 'true'}
                            loading={otpSaving === 'otp_login_enabled'}
                            onChange={val => updateOtpSetting('otp_login_enabled', val)}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col span={24}>
                    <Card type="inner" title="Registrasi Merchant">
                      <Row align="middle" justify="space-between">
                        <Col>
                          <div><strong>OTP saat Registrasi</strong></div>
                          <Text type="secondary">Kirim OTP untuk verifikasi email saat merchant mendaftar. Jika OFF, email langsung terverifikasi otomatis.</Text>
                        </Col>
                        <Col>
                          <Switch
                            checked={otpSettings['otp_registration_enabled'] === 'true'}
                            loading={otpSaving === 'otp_registration_enabled'}
                            onChange={val => updateOtpSetting('otp_registration_enabled', val)}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col span={24}>
                    <Divider>Parameter OTP</Divider>
                    <Row gutter={[16, 16]}>
                      {[
                        { key: 'otp_length', label: 'Panjang Kode OTP', suffix: 'digit' },
                        { key: 'otp_expiry_minutes', label: 'Kadaluarsa OTP', suffix: 'menit' },
                        { key: 'otp_max_attempts', label: 'Maks. Percobaan Salah', suffix: 'kali' },
                        { key: 'otp_lockout_minutes', label: 'Durasi Lockout', suffix: 'menit' },
                        { key: 'otp_rate_limit_per_hour', label: 'Maks. Request per Jam', suffix: 'req' },
                        { key: 'otp_rate_limit_per_day', label: 'Maks. Request per Hari', suffix: 'req' },
                      ].map(({ key, label, suffix }) => (
                        <Col xs={24} sm={12} md={8} key={key}>
                          <div style={{ marginBottom: 4 }}><Text strong>{label}</Text></div>
                          <Space.Compact style={{ width: '100%' }}>
                            <InputNumber
                              style={{ flex: 1 }}
                              value={parseInt(otpSettings[key]) || 0}
                              min={1}
                              onChange={val => setOtpSettings(prev => ({ ...prev, [key]: String(val) }))}
                            />
                            <Button
                              loading={otpSaving === key}
                              onClick={() => updateOtpSetting(key, otpSettings[key])}
                            >
                              Simpan
                            </Button>
                          </Space.Compact>
                          <Text type="secondary" style={{ fontSize: 12 }}>{suffix}</Text>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </Spin>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Settings;